import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Ticket, Send, MessageCircle, Clock, AlertCircle, CheckCircle, ChevronLeft, Loader2, RefreshCw, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

// Sanitize HTML to prevent XSS attacks
const sanitizeHTML = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, { 
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel']
  });
};

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', priority: 'Medium' });
  const [creatingTicket, setCreatingTicket] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
        setLinked(data.linked !== false);
        setAccountName(data.account_name || '');
        if (data.message && !data.linked) {
          toast.info(data.message);
        }
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${ticketId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTicketDetails(data);
      }
    } catch (error) {
      console.error('Failed to load ticket details:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyContent('');
    fetchTicketDetails(ticket.id);
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() || !selectedTicket) return;

    setSubmittingReply(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: replyContent })
      });

      if (response.ok) {
        toast.success('Reply sent successfully');
        setReplyContent('');
        // Refresh ticket details
        fetchTicketDetails(selectedTicket.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreatingTicket(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTicket)
      });

      if (response.ok) {
        toast.success('Ticket created successfully');
        setNewTicket({ subject: '', description: '', priority: 'Medium' });
        setShowNewTicket(false);
        fetchTickets(); // Refresh the tickets list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setCreatingTicket(false);
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'closed' || statusLower === 'resolved') {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    if (statusLower === 'on hold' || statusLower === 'pending') {
      return <Clock className="h-4 w-4 text-yellow-500" />;
    }
    return <AlertCircle className="h-4 w-4 text-primary" />;
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'closed' || statusLower === 'resolved') return 'bg-green-500/10 text-green-500';
    if (statusLower === 'on hold' || statusLower === 'pending') return 'bg-yellow-500/10 text-yellow-500';
    if (statusLower === 'open' || statusLower === 'new') return 'bg-primary/10 text-primary';
    return 'bg-muted text-muted-foreground';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Support">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Support">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Ticket className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-mono font-bold mb-2">Support Tickets</h2>
                <p className="text-sm text-muted-foreground">
                  {linked 
                    ? `View and respond to your support tickets${accountName ? ` for ${accountName}` : ''}.`
                    : 'Contact your administrator to link your account to Zoho Desk.'
                  }
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchTickets}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              {linked && (
                <Button size="sm" onClick={() => setShowNewTicket(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Ticket
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* New Ticket Form Modal */}
        {showNewTicket && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-mono font-bold">Create New Ticket</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowNewTicket(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <Label htmlFor="subject" className="font-mono">Subject *</Label>
                <Input
                  id="subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="mt-1"
                  disabled={creatingTicket}
                />
              </div>
              <div>
                <Label htmlFor="description" className="font-mono">Description *</Label>
                <Textarea
                  id="description"
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Please provide as much detail as possible about your issue..."
                  className="mt-1 min-h-[120px]"
                  disabled={creatingTicket}
                />
              </div>
              <div>
                <Label htmlFor="priority" className="font-mono">Priority</Label>
                <select
                  id="priority"
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md font-mono text-sm"
                  disabled={creatingTicket}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowNewTicket(false)}
                  disabled={creatingTicket}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={creatingTicket}>
                  {creatingTicket ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {!linked ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-mono font-bold mb-2">Not Connected</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Your account is not yet linked to Zoho Desk. Please contact your administrator to set up the connection.
            </p>
          </div>
        ) : selectedTicket ? (
          // Ticket Detail View
          <div className="space-y-4">
            <Button 
              variant="ghost" 
              onClick={() => {
                setSelectedTicket(null);
                setTicketDetails(null);
              }}
              className="mb-2"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Tickets
            </Button>

            {loadingDetails ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                <p className="text-muted-foreground mt-4">Loading ticket details...</p>
              </div>
            ) : ticketDetails ? (
              <>
                {/* Ticket Info */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-mono font-bold">{ticketDetails.ticket?.subject || selectedTicket.subject}</h3>
                      <p className="text-sm text-muted-foreground">
                        Ticket #{ticketDetails.ticket?.ticketNumber || selectedTicket.ticketNumber}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-mono ${getStatusColor(ticketDetails.ticket?.status || selectedTicket.status)}`}>
                      {ticketDetails.ticket?.status || selectedTicket.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground">Created</p>
                      <p className="font-mono text-sm">{formatDate(ticketDetails.ticket?.createdTime)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Priority</p>
                      <p className="font-mono text-sm">{ticketDetails.ticket?.priority || 'Normal'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Channel</p>
                      <p className="font-mono text-sm">{ticketDetails.ticket?.channel || 'Portal'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Last Updated</p>
                      <p className="font-mono text-sm">{formatDate(ticketDetails.ticket?.modifiedTime)}</p>
                    </div>
                  </div>

                  {ticketDetails.ticket?.description && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-2">Description</p>
                      <div 
                        className="text-sm prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(ticketDetails.ticket.description) }}
                      />
                    </div>
                  )}
                </div>

                {/* Conversations */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h4 className="font-mono font-bold mb-4 flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-primary" />
                    Conversation
                  </h4>

                  <div className="space-y-4 max-h-[400px] overflow-y-auto mb-6">
                    {ticketDetails.conversations && ticketDetails.conversations.length > 0 ? (
                      ticketDetails.conversations.map((conv, idx) => (
                        <div 
                          key={conv.id || idx} 
                          className={`p-4 rounded-lg ${conv.isForward ? 'bg-muted/50 ml-8' : 'bg-primary/5 mr-8'}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-sm font-bold">
                              {conv.author?.name || conv.fromEmailAddress || 'Support'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(conv.createdTime)}
                            </span>
                          </div>
                          <div 
                            className="text-sm prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizeHTML(conv.content || conv.summary || '') }}
                          />
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-4">No conversation history yet</p>
                    )}

                    {/* Comments */}
                    {ticketDetails.comments && ticketDetails.comments.length > 0 && (
                      <>
                        <div className="border-t border-border my-4 pt-4">
                          <p className="text-xs text-muted-foreground mb-3">Comments</p>
                        </div>
                        {ticketDetails.comments.map((comment, idx) => (
                          <div 
                            key={comment.id || idx} 
                            className={`p-4 rounded-lg ${comment.isPublic ? 'bg-primary/5' : 'bg-muted/30'}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-mono text-sm font-bold">
                                {comment.commenter?.name || 'Unknown'}
                                {!comment.isPublic && <span className="text-xs text-muted-foreground ml-2">(Internal)</span>}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(comment.createdTime)}
                              </span>
                            </div>
                            <div 
                              className="text-sm"
                              dangerouslySetInnerHTML={{ __html: sanitizeHTML(comment.content || '') }}
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Reply Form */}
                  <form onSubmit={handleSubmitReply} className="border-t border-border pt-4">
                    <Textarea
                      placeholder="Type your reply here..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      className="min-h-[100px] mb-3"
                      disabled={submittingReply}
                    />
                    <div className="flex justify-end">
                      <Button 
                        type="submit" 
                        disabled={submittingReply || !replyContent.trim()}
                      >
                        {submittingReply ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send Reply
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </>
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Could not load ticket details</p>
              </div>
            )}
          </div>
        ) : (
          // Tickets List - Grouped by Status
          <div className="space-y-6">
            {tickets.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-mono font-bold mb-2">No Tickets</h3>
                <p className="text-muted-foreground">You don't have any support tickets yet.</p>
              </div>
            ) : (
              (() => {
                // Group tickets by status
                const statusOrder = ['Open', 'In Progress', 'On Hold', 'Escalated', 'Closed'];
                const groupedTickets = tickets.reduce((acc, ticket) => {
                  const status = ticket.status || 'Unknown';
                  if (!acc[status]) acc[status] = [];
                  acc[status].push(ticket);
                  return acc;
                }, {});
                
                // Sort each group by createdTime (newest first)
                Object.keys(groupedTickets).forEach(status => {
                  groupedTickets[status].sort((a, b) => 
                    new Date(b.createdTime) - new Date(a.createdTime)
                  );
                });
                
                // Order groups by priority (Open first, Closed last)
                const orderedStatuses = Object.keys(groupedTickets).sort((a, b) => {
                  const aIndex = statusOrder.indexOf(a);
                  const bIndex = statusOrder.indexOf(b);
                  if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                  if (aIndex === -1) return 1;
                  if (bIndex === -1) return -1;
                  return aIndex - bIndex;
                });
                
                return orderedStatuses.map(status => (
                  <div key={status} className="space-y-2">
                    <div className="flex items-center gap-2 px-1">
                      {getStatusIcon(status)}
                      <h3 className="font-mono font-bold text-sm uppercase tracking-wider">
                        {status}
                      </h3>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                        {groupedTickets[status].length}
                      </span>
                    </div>
                    <div className="bg-card border border-border rounded-lg divide-y divide-border">
                      {groupedTickets[status].map((ticket) => (
                        <button
                          key={ticket.id}
                          onClick={() => handleSelectTicket(ticket)}
                          className="w-full text-left p-4 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-mono font-bold truncate mb-1">{ticket.subject}</h4>
                              <p className="text-sm text-muted-foreground">
                                #{ticket.ticketNumber} • {formatDate(ticket.createdTime)}
                              </p>
                            </div>
                            {ticket.isArchived && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-mono bg-muted text-muted-foreground">
                                Archived
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ));
              })()
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Support;
